import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { 
  fetchTrendingVideos, 
  likeVideo, 
  unlikeVideo,
  selectTrendingVideos,
  selectVideoLoading,
  selectVideoError
} from '../store/slices/videoSlice';
import VideoFeed from '../components/organisms/VideoFeed';
import CommentModal from '../components/molecules/CommentModal';
import PageTemplate from '../components/templates/PageTemplate';
import { MobileHeaderType } from '../components/layout/ConfigurableMobileHeader';

// Desktop styles
const DesktopContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  overflow: hidden;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none; /* Hide on mobile */
  }
`;

// Mobile styles
const MobileContainer = styled.div`
  display: none;
  width: 100%;
  height: 100vh; /* Full height for immersive experience */
  flex-direction: column;
  align-items: center;
  padding: 0;
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
  background-color: #000;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: flex; /* Show only on mobile */
  }
`;

// Removed BackButton styled component as it's now in TrendingMobileHeader

const DesktopFeedContainer = styled.div`
  width: 100%;
  max-width: 500px;
  height: 100%;
  position: relative;
  background-color: #000;
  border-radius: 12px;
  overflow: hidden;
`;

const MobileFeedContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  background-color: #000;
  overflow: hidden;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  color: #fff;
  font-size: 16px;
`;

const ErrorContainer = styled.div`
  padding: 24px;
  text-align: center;
  color: #f44336;
  font-size: 16px;
  background-color: rgba(244, 67, 54, 0.1);
  border-radius: 8px;
  margin: 24px;
`;

const Trending: React.FC = () => {
  const dispatch = useAppDispatch();
  const videos = useAppSelector(selectTrendingVideos);
  const loading = useAppSelector(selectVideoLoading);
  const error = useAppSelector(selectVideoError);
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [currentVideoId, setCurrentVideoId] = useState<string>('');
  
  useEffect(() => {
    dispatch(fetchTrendingVideos());
  }, [dispatch]);
  
  const handleLike = (videoId: string) => {
    // Check if video is already liked and toggle accordingly
    const video = videos.find(v => v.id === videoId);
    if (video?.isLiked) {
      dispatch(unlikeVideo(videoId));
    } else {
      dispatch(likeVideo(videoId));
    }
  };
  
  const handleComment = (videoId: string) => {
    setCurrentVideoId(videoId);
    setCommentModalOpen(true);
  };
  
  const handleCloseCommentModal = () => {
    setCommentModalOpen(false);
  };
  
  const handleShare = (videoId: string) => {
    // In a real app, this would open a share dialog
    alert(`Sharing video ${videoId}`);
  };
  
  const handleSave = (videoId: string) => {
    // In a real app, this would save the video to user's collection
    alert(`Saving video ${videoId}`);
  };
  
  // Navigation is now handled in TrendingMobileHeader
  
  return (
    <>
      {/* Desktop View */}
      <PageTemplate>
        <DesktopContainer>
          {loading && videos.length === 0 && (
            <LoadingContainer>Loading trending videos...</LoadingContainer>
          )}
          
          {error && (
            <ErrorContainer>Error: {error}</ErrorContainer>
          )}
          
          {!loading && !error && videos.length > 0 && (
            <DesktopFeedContainer>
              <VideoFeed
                videos={videos}
                onLike={handleLike}
                onComment={handleComment}
                onShare={handleShare}
                onSave={handleSave}
              />
            </DesktopFeedContainer>
          )}
        </DesktopContainer>
      </PageTemplate>
      
      {/* Mobile View - with bottom navigation visible and simple back button header */}
      <PageTemplate 
        hideHeaderOnMobile={true} 
        hideSidebarOnMobile={true}
        hideBottomNavOnMobile={false} // Ensure bottom navigation is visible
        mobileHeaderType={MobileHeaderType.SIMPLE}
        showBackButton={true}
        backPath="/enterprises"
        transparentHeader={true}
      >
        <MobileContainer>
          {loading && videos.length === 0 && (
            <LoadingContainer>Loading trending videos...</LoadingContainer>
          )}
          
          {error && (
            <ErrorContainer>Error: {error}</ErrorContainer>
          )}
          
          {!loading && !error && videos.length > 0 && (
            <MobileFeedContainer>
              <VideoFeed
                videos={videos}
                onLike={handleLike}
                onComment={handleComment}
                onShare={handleShare}
                onSave={handleSave}
              />
            </MobileFeedContainer>
          )}
        </MobileContainer>
      </PageTemplate>
      
      {/* Comment Modal - shared between both views */}
      <CommentModal
        videoId={currentVideoId}
        isOpen={commentModalOpen}
        onClose={handleCloseCommentModal}
      />
    </>
  );
};

export default Trending;
